import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Container, Row, Col, Card, Badge, Spinner, Button } from "react-bootstrap";
import { FiFileText, FiMapPin, FiDollarSign, FiCalendar, FiRefreshCw, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const CustomerLease = () => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token = loggedInUser?.refreshToken || authState?.refreshToken || loggedInUser?.token || authState?.token;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchCustomerLeases = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}/lease/leases`,
        {
          page_number: page,
          page_size: 10,
          order_by: "DESC",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = response.data;
      const fetchedLeases = data?.leaseAssets || data?.leases || data?.data || (Array.isArray(data) ? data : []);
      setLeases(fetchedLeases);

      if (data?.totalElements) {
        setTotalPages(Math.ceil(data.totalElements / 10));
      } else if (data?.totalPages) {
        setTotalPages(data.totalPages);
      } else {
        setTotalPages(fetchedLeases.length < 10 ? page : page + 1);
      }
    } catch (error) {
      console.error("Error fetching customer leases:", error);
      setLeases([]);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, token, page]);

  useEffect(() => {
    if (token) {
      fetchCustomerLeases();
    }
  }, [fetchCustomerLeases, token]);

  const getStatusBadge = (status) => {
    const s = String(status || "").toUpperCase();
    switch (s) {
      case "APPROVED":
      case "ACTIVE":
        return <Badge bg="success">Approved</Badge>;
      case "IN_REVIEW":
        return <Badge bg="info">In Review</Badge>;
      case "REJECTED":
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="warning" text="dark">Pending</Badge>;
    }
  };

  return (
    <Container fluid className="p-4">
      {/* Top Header */}
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-4 gap-3">
        <div>
          <h3 className="fw-bold mb-1 text-dark">Lease Management</h3>
          <p className="text-muted mb-0">Track and manage your requested property leases.</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button variant="light" className="border shadow-sm d-flex align-items-center gap-2" onClick={fetchCustomerLeases} disabled={loading}>
            <FiRefreshCw className={loading ? "spin" : ""} /> Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" role="status" />
          <p className="mt-2 text-muted">Loading your leases...</p>
        </div>
      ) : leases.length === 0 ? (
        <Card className="text-center py-5 border-0 shadow-sm rounded-4">
          <Card.Body>
            <FiFileText size={48} className="text-muted mb-3" />
            <h5 className="fw-bold text-dark">No Leases Found</h5>
            <p className="text-muted mb-0">You haven't submitted any lease requests yet.</p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-4">
            {leases.map((item) => (
              <Col xs={12} md={6} lg={4} key={item.id}>
                <Card className="h-100 shadow-sm border-0 rounded-4 overflow-hidden">
                  <Card.Header className="bg-light border-0 p-3 d-flex align-items-center justify-content-between">
                    <span className="badge bg-primary-subtle text-primary fw-semibold px-2 py-1">
                      {item.purpose?.name || "Lease Request"}
                    </span>
                    {getStatusBadge(item.status)}
                  </Card.Header>
                  <Card.Body className="p-3 d-flex flex-column justify-content-between">
                    <div>
                      <h5 className="fw-bold text-dark mb-2 text-truncate" title={item.title}>
                        {item.title || "Lease Request"}
                      </h5>
                      <p className="text-muted small mb-3 text-line-clamp-2">
                        {item.description || "No description provided."}
                      </p>

                      <div className="d-flex flex-column gap-2 mb-3">
                        <div className="d-flex align-items-center text-secondary small">
                          <FiDollarSign className="me-2 text-success" />
                          <span className="fw-semibold text-dark">
                            Budget: ₹{item.budget ? Number(item.budget).toLocaleString() : "N/A"}
                          </span>
                        </div>
                        <div className="d-flex align-items-center text-secondary small">
                          <FiMapPin className="me-2 text-primary" />
                          <span>
                            {[item.city, item.state].filter(Boolean).join(", ") || "Location N/A"}
                          </span>
                        </div>
                        {item.created_on && (
                          <div className="d-flex align-items-center text-secondary small">
                            <FiCalendar className="me-2 text-muted" />
                            <span>
                              {new Date(item.created_on).toLocaleDateString("en-IN", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
              >
                <FiChevronLeft /> Previous
              </Button>
              <span className="small text-muted fw-semibold">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <FiChevronRight />
              </Button>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default CustomerLease;
